import { IMedium } from "../medium/response";
import { IUnitPartition } from "../unit-partition/response";

/**
 * Trip start-check: fulfilled lines whose (actual) product is flagged
 * `requiresTripStartCheck` — shown for mandatory confirmation before the
 * trip can start. Quantity fields mirror IProductsOrder so the existing
 * quantity resolution helpers apply verbatim.
 */

export interface ITripStartCheckUser {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}

export interface ITripStartCheckUnit {
  id?: number;
  name?: { ar?: string | null; en?: string | null } | string | null;
}

export interface ITripStartCheckLine {
  productOrderId: number;
  productId: number;
  productName?: { ar?: string | null; en?: string | null } | null;
  image?: IMedium | null;
  isAlternative: boolean;
  isAddedFromStore?: boolean;
  soldByUnit: boolean;
  storeId?: number | null;
  count?: number | null;
  unitPartition?: IUnitPartition | null;
  quantityByUnit?: number | null;
  quantityAccounting?: number | null;
  reservedQuantityByUnit?: number | null;
  reservedQuantityAccounting?: number | null;
  factorAccountingSystem?: number | null;
  decimalPlaces?: number | null;
  /**
   * Base product unit. Older responses may omit it; the UI then falls back to
   * unitPartition.unit, never to the partition label itself.
   */
  unit?: ITripStartCheckUnit | null;
}

export interface ITripStartCheckOrder {
  orderId: number;
  orderNumber: string;
  clientName?: string | null;
  lines: ITripStartCheckLine[];
}

interface ITripStartCheckBase {
  confirmedBy: ITripStartCheckUser | null;
  confirmedAt: string | null;
  orders: ITripStartCheckOrder[];
}

/** The token exists only while a human confirmation is currently required. */
export type ITripStartCheck =
  | (ITripStartCheckBase & {
      required: true;
      startCheckToken: string;
    })
  | (ITripStartCheckBase & {
      required: false;
      startCheckToken?: never;
    });

export enum TripStartCheckErrorKey {
  REQUIRED = "trip_start_check_required",
  TOKEN_REQUIRED = "trip_start_check_token_required",
  STALE = "trip_start_check_stale",
  PENDING_TRIP_REQUIRED = "trip_start_check_pending_trip_required",
  SAVE_CHANGES_FIRST = "trip_start_save_changes_first",
  ACTIVE_STRUCTURE_LOCKED = "trip_active_structure_locked",
}

export const TRIP_START_CHECK_REQUIRED_ERROR_KEY =
  TripStartCheckErrorKey.REQUIRED;

export const getTripStartCheckErrorKey = (
  error: any,
): TripStartCheckErrorKey | undefined => {
  const key = error?.response?.data?.errorKey;
  return Object.values(TripStartCheckErrorKey).includes(key)
    ? (key as TripStartCheckErrorKey)
    : undefined;
};

export const getTripStartCheckErrorText = (error: any): string | undefined => {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && typeof errors[0] === "string"
    ? errors[0]
    : undefined;
};

export const isTripStartCheckRefreshError = (error: any): boolean => {
  const key = getTripStartCheckErrorKey(error);
  return (
    key === TripStartCheckErrorKey.REQUIRED ||
    key === TripStartCheckErrorKey.TOKEN_REQUIRED ||
    key === TripStartCheckErrorKey.STALE
  );
};

/** Runtime guard for server data; keeps the safety gate closed on bad JSON. */
export const parseTripStartCheck = (value: unknown): ITripStartCheck => {
  if (!value || typeof value !== "object") {
    throw new Error("Invalid trip start-check response");
  }

  const check = value as Record<string, unknown>;
  if (typeof check.required !== "boolean" || !Array.isArray(check.orders)) {
    throw new Error("Invalid trip start-check response");
  }

  if (
    check.required === true &&
    (typeof check.startCheckToken !== "string" ||
      check.startCheckToken.trim().length === 0)
  ) {
    throw new Error("Missing trip start-check token");
  }

  return value as ITripStartCheck;
};
